import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, UserPlus, X, Loader2, User, Mail, Phone, Save, Calendar, Clock, ExternalLink, ShieldCheck, ShieldAlert, AlertTriangle, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { ghlApi } from '../../lib/ghlApi';
import { useAppointmentBooking } from '../../hooks/useAppointmentBooking';

export function AddAppointmentDialog(props) {
  const b = useAppointmentBooking(props);
  const { open, prefill } = props;
  const {
    tab, setTab, searchQuery, setSearchQuery, searchResults, setSearchResults, searching,
    selectedContact, setSelectedContact, contactSource, setContactSource, showContactForm,
    contactForm, setContactForm, contactDirty, updatingContact, juvonnoId, setJuvonnoId,
    verifyStatus, verifyDetails, verifiedAt, setVerifyStatus, setVerifyDetails, setVerifiedAt,
    calendarId, setCalendarId, date, setDate, time, setTime,
    appointmentType, setAppointmentType, duration, setDuration,
    submitting, notes, setNotes, genderFieldId, customFieldDefs,
    showForceSave, setShowForceSave, lastError, errorType,
    savingNewContact, dupMatches, dupSearching,
    currentLocation, user,
    handleDialogOpenChange, handleSelectContact, handleUpdateContact,
    handleVerifyJuvonno, handleSubmit,
    handleSaveNewContact, attemptCloseContactForm,
    updateField, getPhoneDigits, doVerify, openJuvonnoProfile,
    DURATION_OPTIONS, TIME_SLOTS, fullName, EMPTY_FORM, JUVONNO_FIELD_KEY,
    calendars, appointmentTypes, locationId,
  } = b;

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-[920px] max-h-[92vh] p-0 gap-0 overflow-hidden rounded-xl flex flex-col" onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} data-testid="book-appointment-dialog">
        <DialogTitle className="sr-only">Book Appointment</DialogTitle>
        <DialogDescription className="sr-only">Schedule a new appointment on the calendar</DialogDescription>
        <div className="px-6 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight" id="book-appointment-title">Book Appointment</h2>
          <p className="text-sm text-gray-500 mt-0.5">Schedule a new appointment on the calendar.</p>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* LEFT: Contact */}
          <div className="w-[440px] border-r flex flex-col bg-gray-50/30 min-h-0">
            <div className="px-5 pt-4 pb-2">
              <h3 className="text-[11px] font-bold text-blue-700 uppercase tracking-widest">Contact Information</h3>
            </div>

            {!showContactForm ? (
              <div className="px-5 flex-1 flex flex-col">
                <p className="text-xs text-gray-500 mb-3">Search for an existing contact.</p>
                <div className="flex gap-0 mb-3">
                  <button onClick={() => setTab('search')} className={cn('flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border rounded-l-lg transition-all', tab === 'search' ? 'bg-gray-900 text-white border-gray-900 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')} data-testid="tab-search-contacts">
                    <Search className="h-3.5 w-3.5" /> Search Contacts
                  </button>
                  <button onClick={() => { setTab('create'); setContactForm({ ...EMPTY_FORM }); setSelectedContact(null); setContactSource(null); setJuvonnoId(''); setVerifyStatus(null); }} className={cn('flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold border-t border-b border-r rounded-r-lg transition-all', tab === 'create' ? 'bg-gray-900 text-white border-gray-900 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')} data-testid="tab-create-contact">
                    <UserPlus className="h-3.5 w-3.5" /> Create New Contact
                  </button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name, email or phone..." className="pl-9 h-10 bg-white" data-testid="contact-search-input" autoFocus />
                  {searchQuery && <button onClick={() => { setSearchQuery(''); setSearchResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>}
                </div>
                <div className="mt-2 flex-1 overflow-y-auto max-h-[380px] space-y-1.5">
                  {/* Duplicate contact alert */}
                  {errorType === 'duplicate_contact' && lastError && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mb-2" data-testid="duplicate-contact-alert">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-amber-800">Patient Already Exists</p>
                          <p className="text-xs text-amber-600 mt-0.5">Select the existing patient below to proceed with booking.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {searching && <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Searching contacts...</div>}
                  {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                    <div className="text-center py-8"><p className="text-gray-400 text-sm">No contacts found</p>
                      <button onClick={() => {
                        const raw = searchQuery.trim();
                        const isPhone = /^[\d\s\(\)\-\+]+$/.test(raw.replace(/\s/g, ''));
                        setTab('create');
                        if (isPhone) {
                          setContactForm({ ...EMPTY_FORM, phone: raw.replace(/[\s\(\)\-]/g, '') });
                        } else {
                          const parts = raw.split(/\s+/).filter(Boolean);
                          const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);
                          setContactForm({ ...EMPTY_FORM, firstName: titleCase(parts[0] || ''), lastName: parts.slice(1).map(w => titleCase(w)).join(' ') || '' });
                        }
                      }} className="text-xs text-blue-600 hover:underline mt-2">Create new contact</button>
                    </div>
                  )}
                  {searchResults.map((c) => (
                    <button key={c.id} onClick={() => handleSelectContact(c)} className="w-full text-left p-3 rounded-lg border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-all flex items-start gap-3 shadow-sm hover:shadow" data-testid={`contact-result-${c.id}`}>
                      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-blue-600" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-gray-800 truncate">{(c.contactName || `${c.firstName || ''} ${c.lastName || ''}`.trim()).split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                        {c.email && <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3" /> {c.email}</p>}
                        {c.phone && <p className="text-[11px] text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="px-5 pb-2 flex-1 overflow-y-auto min-h-0">
                  {selectedContact ? (
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-blue-600" /></div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">Editing Contact</p>
                          <p className="text-[10px] text-gray-400 truncate">ID: {selectedContact.id?.slice(0, 12)}…</p>
                        </div>
                      </div>
                      <button
                        onClick={() => attemptCloseContactForm(() => { setSelectedContact(null); setContactSource(null); setContactForm({ ...EMPTY_FORM }); setTab('search'); setJuvonnoId(''); setVerifyStatus(null); })}
                        className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
                        title="Deselect contact"
                        data-testid="deselect-contact-btn"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center flex-shrink-0"><UserPlus className="h-4 w-4 text-emerald-600" /></div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">New Contact</p>
                          <p className="text-[10px] text-gray-400 truncate">Enter patient details</p>
                        </div>
                      </div>
                      <button
                        onClick={() => attemptCloseContactForm(() => { setTab('search'); setContactForm({ ...EMPTY_FORM }); })}
                        className="text-xs text-blue-600 hover:underline flex-shrink-0 font-medium"
                        data-testid="back-to-search-btn"
                      >
                        &larr; Back to search
                      </button>
                    </div>
                  )}

                <div className="space-y-3">
                  {/* Required-fields progress badge */}
                  {(() => {
                    const required = [
                      { label: 'First Name', ok: !!contactForm.firstName?.trim(), testid: 'contact-first-name' },
                      { label: 'Last Name', ok: !!contactForm.lastName?.trim(), testid: 'contact-last-name' },
                      { label: 'Phone', ok: !!contactForm.phone?.trim(), testid: 'contact-phone' },
                      { label: 'Email', ok: !!contactForm.email?.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contactForm.email.trim()), testid: 'contact-email' },
                      { label: 'Gender', ok: !!contactForm.gender, testid: 'contact-gender' },
                      { label: 'Date of Birth', ok: !!contactForm.dateOfBirth, testid: 'contact-dob' },
                    ];
                    const filled = required.filter(r => r.ok).length;
                    const total = required.length;
                    const allDone = filled === total;
                    const missing = required.filter(r => !r.ok);
                    const focusFirstMissing = () => {
                      if (!missing.length) return;
                      const el = document.querySelector(`[data-testid="${missing[0].testid}"]`);
                      if (!el) return;
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      // Native inputs/textareas focus directly; Radix Select trigger is a button
                      try { el.focus({ preventScroll: true }); } catch {}
                      // Briefly highlight to draw attention
                      el.classList.add('ring-2', 'ring-amber-400');
                      setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 1400);
                    };
                    return (
                      <button
                        type="button"
                        onClick={focusFirstMissing}
                        disabled={allDone}
                        className={cn(
                          'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-[11px] font-semibold transition-colors text-left',
                          allDone
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default'
                            : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 cursor-pointer'
                        )}
                        title={allDone ? '' : `Click to jump to ${missing[0].label}`}
                        data-testid="required-progress-badge"
                      >
                        <span className="flex items-center gap-1.5">
                          {allDone
                            ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                            : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
                          {allDone ? 'All required fields complete' : `${filled} of ${total} required fields filled`}
                        </span>
                        {!allDone && (
                          <span className="text-[10px] font-normal text-amber-600 truncate flex items-center gap-1" title={missing.map(m => m.label).join(' • ')}>
                            Missing: {missing.slice(0, 2).map(m => m.label).join(', ')}{missing.length > 2 ? ` +${missing.length - 2}` : ''}
                            <span className="text-amber-700 font-semibold">→ Jump</span>
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow label="First Name" required>
                      <Input
                        value={contactForm.firstName}
                        onChange={(e) => updateField('firstName', e.target.value)}
                        placeholder="John"
                        data-testid="contact-first-name"
                        className={cn(!contactForm.firstName?.trim() && 'border-red-300 bg-red-50',
                          contactForm.firstName && /^\(?[\d\s\-\+\)]+$/.test(contactForm.firstName.trim()) && 'border-red-300 bg-red-50')}
                      />
                      {!contactForm.firstName?.trim() && <p className="text-[10px] text-red-600 mt-1 font-medium">First name is required</p>}
                      {contactForm.firstName && /^\(?[\d\s\-\+\)]+$/.test(contactForm.firstName.trim()) && <p className="text-[10px] text-red-600 mt-1 font-medium">Cannot be a phone number</p>}
                    </FieldRow>
                    <FieldRow label="Last Name" required>
                      <Input
                        value={contactForm.lastName}
                        onChange={(e) => updateField('lastName', e.target.value)}
                        placeholder="Smith"
                        data-testid="contact-last-name"
                        className={cn(!contactForm.lastName?.trim() && 'border-red-300 bg-red-50',
                          contactForm.lastName && /^\(?[\d\s\-\+\)]+$/.test(contactForm.lastName.trim()) && 'border-red-300 bg-red-50')}
                      />
                      {!contactForm.lastName?.trim() && <p className="text-[10px] text-red-600 mt-1 font-medium">Last name is required</p>}
                      {contactForm.lastName && /^\(?[\d\s\-\+\)]+$/.test(contactForm.lastName.trim()) && <p className="text-[10px] text-red-600 mt-1 font-medium">Cannot be a phone number</p>}
                    </FieldRow>
                  </div>
                  <FieldRow label="Phone" required>
                    <Input value={contactForm.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="(613) 869-6399" data-testid="contact-phone" />
                  </FieldRow>
                  <FieldRow label="Email" required>
                    <Input type="email" value={contactForm.email} onChange={(e) => updateField('email', e.target.value)} placeholder="john@example.com" data-testid="contact-email" className={cn(contactForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contactForm.email.trim()) && 'border-red-300 bg-red-50')} />
                    {contactForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contactForm.email.trim()) && <p className="text-[10px] text-red-600 mt-1 font-medium">Please enter a valid email address</p>}
                  </FieldRow>

                  {/* Duplicate contact detection */}
                  {!selectedContact && dupMatches.length > 0 && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="duplicate-matches-panel">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-bold text-amber-800">Existing contact found</p>
                          <p className="text-[10px] text-amber-600">A contact with this email or phone already exists. Select to use instead.</p>
                        </div>
                      </div>
                      <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                        {dupMatches.map(c => (
                          <button key={c.id} onClick={() => handleSelectContact(c)}
                            className="w-full text-left p-2 rounded-lg border border-amber-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center gap-2"
                            data-testid={`dup-contact-${c.id}`}
                          >
                            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center flex-shrink-0">
                              <User className="h-3.5 w-3.5 text-amber-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-[11px] text-gray-800 truncate">{(c.contactName || `${c.firstName || ''} ${c.lastName || ''}`.trim()).split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                {c.email && <span className="truncate">{c.email}</span>}
                                {c.phone && <span>{c.phone}</span>}
                              </div>
                            </div>
                            <span className="text-[9px] font-semibold text-blue-600 flex-shrink-0">Select</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {!selectedContact && dupSearching && (
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 px-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Checking for existing contacts...
                    </div>
                  )}

                  <FieldRow label="Gender" required>
                    <Select value={contactForm.gender} onValueChange={(v) => updateField('gender', v)}>
                      <SelectTrigger data-testid="contact-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Date of Birth" required>
                    <Input type="date" value={contactForm.dateOfBirth} onChange={(e) => updateField('dateOfBirth', e.target.value)} data-testid="contact-dob" />
                  </FieldRow>

                  {/* Juvonno Customer ID */}
                  {selectedContact && (
                    <div>
                      <Label className="text-[11px] font-semibold text-gray-600 mb-1 block">Juvonno Customer ID</Label>
                      <div className="flex items-center gap-2">
                        <Input value={juvonnoId} readOnly className="flex-1 bg-gray-50 text-gray-700 font-mono" placeholder="Not set" data-testid="juvonno-id" />
                        {juvonnoId && (
                          <>
                            <button onClick={openJuvonnoProfile} className="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors" title="Open Juvonno Profile" data-testid="open-juvonno-profile">
                              <ExternalLink className="h-4 w-4 text-blue-600" />
                            </button>
                            <button onClick={handleVerifyJuvonno} disabled={verifyStatus === 'loading'} className={cn('h-9 w-9 flex items-center justify-center rounded-lg border transition-colors', verifyStatus === 'verified' ? 'border-green-400 bg-green-100' : verifyStatus === 'mismatch' ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white hover:bg-emerald-50 hover:border-emerald-300')} title={verifyStatus === 'verified' && verifiedAt ? `Verified on ${new Date(verifiedAt).toLocaleString()}` : 'Verify patient in Juvonno'} data-testid="verify-juvonno">
                              {verifyStatus === 'loading' ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : verifyStatus === 'verified' ? <ShieldCheck className="h-4 w-4 text-green-600" /> : (verifyStatus === 'mismatch' || verifyStatus === 'found_correct') ? <ShieldAlert className="h-4 w-4 text-red-600" /> : <ShieldCheck className="h-4 w-4 text-gray-400" />}
                            </button>
                            {verifyStatus === 'verified' && verifiedAt && (
                              <span className="text-[10px] text-green-600 font-medium whitespace-nowrap" title={new Date(verifiedAt).toLocaleString()} data-testid="verified-timestamp">
                                Verified {new Date(verifiedAt).toLocaleDateString()}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {/* Verify result */}
                      {verifyStatus === 'verified' && (
                        <div className="mt-2 p-2 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2" data-testid="verify-success">
                          <ShieldCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-xs text-green-700 font-medium">
                            Patient verified — details match Juvonno record.
                            {verifiedAt && <span className="text-green-500 font-normal ml-1">({new Date(verifiedAt).toLocaleString()})</span>}
                          </span>
                        </div>
                      )}
                      {verifyStatus === 'confirm-reverify' && (
                        <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200" data-testid="verify-reverify-prompt">
                          <p className="text-xs text-amber-800 font-medium mb-1.5">Already verified. Would you like to re-verify?</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2.5" onClick={() => doVerify()}>Yes, re-verify</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2.5" onClick={() => setVerifyStatus('verified')}>No, keep result</Button>
                          </div>
                        </div>
                      )}
                      {verifyStatus === 'mismatch' && verifyDetails && (
                        <div className="mt-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="verify-mismatch">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                            <span className="text-xs text-red-700 font-bold">Profile mismatch — correct before booking</span>
                          </div>
                          {verifyDetails.mismatches?.map((m, i) => (
                            <div key={i} className="text-[11px] text-red-700 ml-6 mb-0.5">
                              <span className="font-bold">{m.field}:</span> CRM: <span className="font-medium">"{m.crm}"</span> → Juvonno: <span className="font-medium">"{m.juvonno}"</span>
                            </div>
                          ))}
                          {verifyDetails.juvonno_patient && (
                            <div className="mt-2 ml-6 p-2 bg-white rounded border border-slate-200 text-[10px] text-slate-700">
                              <div className="font-bold text-slate-800 mb-1">Juvonno Profile (ID: {juvonnoId}):</div>
                              <div>Name: <span className="font-semibold">{verifyDetails.juvonno_patient.name}</span></div>
                              <div>Email: <span className="font-semibold">{verifyDetails.juvonno_patient.email}</span></div>
                              <div>Phone: <span className="font-semibold">{verifyDetails.juvonno_patient.phone}</span></div>
                              <div>DOB: <span className="font-semibold">{verifyDetails.juvonno_patient.date_of_birth}</span></div>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2 ml-6">
                            {currentLocation?.juvonno_portal_url && juvonnoId && (
                              <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => { window.open(`${currentLocation.juvonno_portal_url}/patient/profile.php?txtCustomerID=${juvonnoId}`, '_blank'); }}>
                                <ExternalLink className="h-3 w-3" /> Open Juvonno
                              </Button>
                            )}
                            {verifyDetails.juvonno_patient && (
                              <Button size="sm" className="h-7 text-[10px] gap-1 bg-amber-600 hover:bg-amber-700" onClick={async () => {
                                // Update CRM with correct Juvonno data (name, email, phone, DOB, gender)
                                try {
                                  const jp = verifyDetails.juvonno_patient;
                                  const parts = (jp.name || '').trim().split(/\s+/).filter(Boolean);
                                  const newFirst = parts[0] || '';
                                  const newLast = parts.slice(1).join(' ') || '';
                                  setContactForm(prev => ({
                                    ...prev,
                                    firstName: newFirst || prev.firstName,
                                    lastName: newLast || prev.lastName,
                                    email: jp.email || prev.email,
                                    phone: jp.phone || prev.phone,
                                    dateOfBirth: jp.date_of_birth || prev.dateOfBirth,
                                    gender: jp.gender || prev.gender,
                                  }));
                                  if (selectedContact?.id) {
                                    await ghlApi.updateContact(selectedContact.id, locationId, {
                                      first_name: newFirst, last_name: newLast,
                                      email: jp.email, phone: jp.phone, date_of_birth: jp.date_of_birth,
                                      gender: jp.gender || undefined,
                                    });
                                  }
                                  setVerifyStatus('verified');
                                  setVerifiedAt(new Date().toISOString());
                                  if (selectedContact?.id) await ghlApi.markJuvonnoVerified(locationId, selectedContact.id, juvonnoId);
                                  toast.success('CRM profile updated & verified');
                                } catch { toast.error('Failed to update CRM'); }
                              }}>
                                <Check className="h-3 w-3" /> Confirm & Update CRM
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={async () => {
                              try {
                                toast.info('Searching Juvonno...');
                                const searchResp = await ghlApi.searchJuvonnoPatients(locationId, {
                                  email: contactForm.email, phone: contactForm.phone, name: fullName(contactForm),
                                });
                                const results = searchResp.data?.patients || [];
                                if (results.length > 0) {
                                  const correct = results[0];
                                  const correctId = String(correct.id);
                                  const correctName = `${correct.first_name || ''} ${correct.last_name || ''}`.trim();
                                  // Show the found profile and ask to confirm
                                  setVerifyStatus('found_correct');
                                  setVerifyDetails({ verified: false, found_profile: { id: correctId, name: correctName, email: correct.email, phone: correct.phone, date_of_birth: correct.date_of_birth, gender: correct.gender } });
                                  toast.success(`Found: ${correctName} (Juvonno ID: ${correctId})`);
                                } else {
                                  toast.error('No matching patient found in Juvonno');
                                }
                              } catch { toast.error('Juvonno search failed'); }
                            }}>
                              <Search className="h-3 w-3" /> Search Juvonno for Correct Profile
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Found correct Juvonno profile — prompt to save */}
                  {verifyStatus === 'found_correct' && verifyDetails?.found_profile && (
                    <div className="mt-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-xs text-emerald-800 font-bold">Correct Juvonno profile found</span>
                      </div>
                      <div className="ml-6 p-2 bg-white rounded border border-slate-200 text-[10px] text-slate-700 mb-2">
                        <div><span className="font-bold">Name:</span> {verifyDetails.found_profile.name}</div>
                        <div><span className="font-bold">Email:</span> {verifyDetails.found_profile.email}</div>
                        <div><span className="font-bold">Phone:</span> {verifyDetails.found_profile.phone}</div>
                        <div><span className="font-bold">DOB:</span> {verifyDetails.found_profile.date_of_birth}</div>
                        <div><span className="font-bold">Juvonno ID:</span> {verifyDetails.found_profile.id}</div>
                        {currentLocation?.juvonno_portal_url && <a href={`${currentLocation.juvonno_portal_url}/patient/profile.php?txtCustomerID=${verifyDetails.found_profile.id}`} target="_blank" rel="noreferrer" className="text-indigo-600 underline font-semibold mt-1 inline-block">Open in Juvonno</a>}
                      </div>
                      <div className="ml-6">
                        <Button size="sm" className="h-8 text-[11px] gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={async () => {
                          try {
                            const correctId = verifyDetails.found_profile.id;
                            setJuvonnoId(correctId);
                            if (selectedContact?.id) {
                              // Existing contact — update Juvonno ID
                              const juvField = customFieldDefs.find(f => f.fieldKey === JUVONNO_FIELD_KEY);
                              if (juvField) {
                                await ghlApi.updateContact(selectedContact.id, locationId, {
                                  custom_fields: [{ id: juvField.id, field_value: correctId }],
                                });
                              }
                              await ghlApi.markJuvonnoVerified(locationId, selectedContact.id, correctId);
                            } else {
                              // New contact — create contact first, then save Juvonno ID
                              const fName = (contactForm.firstName || '').trim();
                              const lName = (contactForm.lastName || '').trim();
                              const resp = await ghlApi.createContact(locationId, {
                                first_name: fName, last_name: lName,
                                name: `${fName} ${lName}`.trim(),
                                email: contactForm.email || undefined, phone: getPhoneDigits() || undefined,
                                gender: contactForm.gender || undefined, date_of_birth: contactForm.dateOfBirth || undefined,
                              });
                              const newId = resp.data?.contact?.id;
                              if (newId) {
                                setSelectedContact({ id: newId });
                                setContactSource('created');
                                const juvField = customFieldDefs.find(f => f.fieldKey === JUVONNO_FIELD_KEY);
                                if (juvField) {
                                  await ghlApi.updateContact(newId, locationId, { custom_fields: [{ id: juvField.id, field_value: correctId }] });
                                }
                                await ghlApi.markJuvonnoVerified(locationId, newId, correctId);
                                toast.success('Contact created with Juvonno ID linked');
                              }
                            }
                            setVerifyStatus('verified');
                            setVerifiedAt(new Date().toISOString());
                            toast.success('Juvonno ID saved. Ready to book.');
                          } catch { toast.error('Failed to save'); }
                        }}>
                          <Check className="h-3.5 w-3.5" /> Save to CRM & Proceed
                        </Button>
                      </div>
                    </div>
                  )}

                  <FieldRow label="Assessment Notes">
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for visit, intake observations, follow-up instructions..." className="w-full border rounded-lg px-3 py-2 text-sm min-h-[72px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 bg-white" data-testid="assessment-notes" />
                  </FieldRow>
                </div>
              </div>

                {/* Sticky footer — Save to CRM button. Pinned at bottom so it's
                    always reachable regardless of form scroll position. */}
                <div className="px-5 py-3 border-t bg-white flex-shrink-0 shadow-[0_-2px_8px_-4px_rgba(15,23,42,0.06)]">
                  {selectedContact ? (
                    <Button
                      onClick={handleUpdateContact}
                      disabled={!contactDirty || updatingContact || !contactForm.firstName?.trim() || !contactForm.lastName?.trim()}
                      className="w-full h-11 text-sm font-bold tracking-wide rounded-lg shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                      data-testid="update-contact-btn"
                    >
                      {updatingContact ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      {contactDirty ? 'Save Changes to CRM' : 'No Changes to Save'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSaveNewContact}
                      disabled={savingNewContact || !contactForm.firstName?.trim() || !contactForm.lastName?.trim()}
                      className="w-full h-11 text-sm font-bold tracking-wide rounded-lg shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                      data-testid="save-new-contact-btn"
                    >
                      {savingNewContact ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save to CRM
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* RIGHT: Appointment Details */}
          <div className="flex-1 flex flex-col bg-white min-h-0">
            <div className="px-5 pt-4 pb-2 flex-shrink-0">
              <h3 className="text-[11px] font-bold text-blue-700 uppercase tracking-widest">Appointment Details</h3>
            </div>
            <div className="px-5 pb-4 space-y-5 flex-1 overflow-y-auto min-h-0">
              <DetailRow icon={User} label="Calendar">
                <Select value={calendarId} onValueChange={setCalendarId}>
                  <SelectTrigger data-testid="select-calendar" className="h-10"><SelectValue placeholder="Select calendar" /></SelectTrigger>
                  <SelectContent>{calendars.filter(cal => cal.isActive !== false).map((cal) => (<SelectItem key={cal.id} value={cal.id}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: cal.color || '#6366f1' }} />{cal.name}</span></SelectItem>))}</SelectContent>
                </Select>
              </DetailRow>
              <DetailRow icon={Calendar} label="Date">
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" data-testid="select-date" />
              </DetailRow>
              <DetailRow icon={Clock} label="Time">
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="select-time" className="h-10"><SelectValue placeholder="Select time" /></SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </DetailRow>
              <DetailRow label="Type">
                <Select value={appointmentType} onValueChange={setAppointmentType}>
                  <SelectTrigger data-testid="select-type" className="h-10"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{appointmentTypes.map((t) => (<SelectItem key={t.id || t.name} value={t.name}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color || '#6366f1' }} />{t.name}</span></SelectItem>))}</SelectContent>
                </Select>
              </DetailRow>
              <DetailRow icon={Clock} label="Duration">
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger data-testid="select-duration" className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{DURATION_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}</SelectContent>
                </Select>
              </DetailRow>
            </div>
            <div className="px-5 py-3 border-t bg-white flex-shrink-0 shadow-[0_-2px_8px_-4px_rgba(15,23,42,0.06)]">
              {/* Error / Conflict Alert */}
              {lastError && (
                <div className={cn(
                  'mb-3 p-3 rounded-lg border flex items-start gap-2',
                  showForceSave
                    ? errorType === 'outside_hours' ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                )} data-testid="booking-error-alert">
                  <AlertCircle className={cn('h-4 w-4 flex-shrink-0 mt-0.5',
                    showForceSave
                      ? errorType === 'outside_hours' ? 'text-orange-500' : 'text-red-500'
                      : 'text-amber-500'
                  )} />
                  <div>
                    <p className={cn('text-sm font-bold',
                      showForceSave
                        ? errorType === 'outside_hours' ? 'text-orange-800' : 'text-red-800'
                        : 'text-amber-700'
                    )}>
                      {showForceSave
                        ? errorType === 'existing_conflict'
                          ? 'Schedule Conflict'
                          : 'Outside Working Hours'
                        : 'Error'}
                    </p>
                    <p className={cn('text-xs mt-1',
                      showForceSave
                        ? errorType === 'outside_hours' ? 'text-orange-600' : 'text-red-600'
                        : 'text-amber-600'
                    )}>
                      {showForceSave
                        ? errorType === 'existing_conflict'
                          ? 'An appointment is already booked at this time.'
                          : 'This time is outside the practitioner\'s scheduled working hours.'
                        : lastError}
                    </p>
                    {showForceSave && !prefill?.forceBook && (
                      <p className={cn('text-[11px] font-medium mt-1.5',
                        errorType === 'outside_hours' ? 'text-orange-500' : 'text-red-500'
                      )}>
                        Click "Force Save" to book anyway.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <Button
                className={cn(
                  'w-full h-11 text-sm font-bold tracking-wide rounded-lg shadow-sm',
                  showForceSave && errorType !== 'outside_hours' && !prefill?.forceBook && 'bg-red-600 hover:bg-red-700'
                )}
                variant={showForceSave && errorType !== 'outside_hours' && !prefill?.forceBook ? 'destructive' : 'default'}
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="create-appointment-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {showForceSave && !prefill?.forceBook ? 'Force Save' : 'Create Appointment'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold text-gray-600 mb-1 block">{label} {required && <span className="text-red-500">*</span>}</Label>
      {children}
    </div>
  );
}

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-4">
      <Label className="w-[72px] text-right text-sm font-semibold text-gray-500 flex items-center justify-end gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-gray-400" />}{label}
      </Label>
      <div className="flex-1">{children}</div>
    </div>
  );
}
